import React from "react"
import { useSelector } from "react-redux"
import styled from "styled-components"

import { ASC } from "const"
import { OrderType } from "client/utils"
import { StoreType } from "client/store"
import {
    ChevronDownIcon,
    ChevronUpIcon,
    Bars,
    PlusIcon,
    MinusIcon,
    CogIcon,
} from "../icons"


const rowSize = {
    fit: "fit-content",
    sm: "40px",
    md: "60px",
}
const bottomHeight = "50px"

export type RowSizeType = typeof rowSize

export interface CellType {
    cellWidth?: string // fixed cell width in pixels
    cellMinWidth?: string
    cellMaxWidth?: string // feature: table cell doesn't handle the exact value of "max-width", but anyway it will limit long strings
    centerAlign?: boolean
    dangerType?: boolean
    isEditable?: boolean
    overflowVisible?: boolean
}

export const TableTitle = styled.label`
    padding: 15px 15px 5px;
    margin: 0;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 12px;
    font-family: ${(props) => props.theme.openSans};
    color: ${(props) => props.theme.defaultTextColor};
`
export const TableSearchWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
    height: 50px;
    background: ${(props) => props.theme.tableRowBackground};
    border-bottom: 1px solid ${(props) => props.theme.tableRowBorderColor};
`
export const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;

    &.disabled {
        opacity: 0.7;
        pointer-events: "none";
    }
`
export const TableScroller = styled.div<{
    showScroll?: boolean
}>`
    overflow-x: ${(props) => (props.showScroll === false ? "hidden" : "scroll")};
`
export const TableContentWrapper = styled.table<{
    inverted?: boolean
}>`
    min-width: 100%;
    color: ${(props) => props.theme.tableColor};

    ${(props) => props.inverted && `
        display: flex;

        & > tbody {
            display: flex;
            flex-grow: 1;
        }
    `}
`
export const TableHeaderCell = styled.th<CellType>`
    cursor: pointer;
    position: relative;
    font-family: ${(props) => props.theme.openSans};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    padding: 8px 12px;
    width: ${(props) => props.cellWidth || "auto"};
    min-width: ${(props) => props.cellMinWidth || "unset"};
    max-width: ${(props) => props.cellMaxWidth || "unset"};
    vertical-align: middle;
    user-select: none;

    &::after {
        position: absolute;
        content: "";
        display: block;
        height: 24px;
        width: 1px;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        opacity: 0;
        background: ${(props) => props.theme.tableHeaderAfterBackground};
        transition: ${(props) => props.theme.defaultTransition};
    }

    &:hover {
        &::after, .sort {
            opacity: 1;
            transition: ${(props) => props.theme.defaultTransition};
        }
    }
`
export const TableCellContentWrapper = styled.div<{ // required for delete animation
    columnDirection?: boolean
    overflowVisible?: boolean
}>`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: ${(props) => (props.columnDirection ? "column" : "row")};
    align-items: ${(props) => (props.columnDirection ? "start" : "center")};
    padding: 8px 12px;
    overflow: ${(props) => (props.overflowVisible ? "visible" : "hidden")};
`
export const TableCellContent = styled.div<{
    fitContent?: boolean
    overflowVisible?: boolean
}>`
    width: 100%;
    line-height: 21px;
    overflow: ${(props) => (props.overflowVisible ? "visible" : "hidden")};
    white-space: ${(props) => (props.fitContent ? "inherit" : "nowrap")};
    text-overflow: ellipsis;
`
export const TableCell = styled.td<CellType>`
    position: relative;
    padding: 0;
    overflow: ${(props) => (props.overflowVisible ? "visible" : "hidden")};
    vertical-align: middle;
    width: ${(props) => props.cellWidth || "auto"};
    min-width: ${(props) => props.cellMinWidth || "unset"};
    max-width: ${(props) => props.cellMaxWidth || "unset"};
    background: ${(props) => props.dangerType && props.theme.tableCellDangerBackground};
    border: ${(props) => (props.isEditable && `2px solid ${props.theme.tableEditableCellBorderColor} !important`)};

    ${(props) => props.centerAlign && `
        & ${TableCellContent} {
            text-align: center;
        }
    `}
`
export const TableRow = styled.tr<{
    size: keyof RowSizeType
    rowSelected?: boolean
    sectionLevel?: 1 | 2
    rowHidden?: boolean
    transition?: number
    noHover?: boolean
    inverted?: boolean
}>`
    height: ${(props) => rowSize[props.size]};
    background: ${(props) => {
        if (props.rowSelected) return props.theme.tableRowSelectedBackground
        if (props.sectionLevel === 1) return props.theme.tableSectionMinorBackground
        if (props.sectionLevel === 2) return props.theme.tableSectionSecondaryBackground
        return props.theme.tableRowBackground
    }};
    transition: ${(props) => (props.transition ? `${props.transition}ms` : "none")};

    & > ${TableCell} {
        font-family: ${(props) => (props.sectionLevel ? props.theme.openSans : "inherit")};
        font-weight: ${(props) => (props.sectionLevel ? "bold" : "inherit")};
        cursor: ${(props) => (props.sectionLevel ? "pointer" : "auto")};
        color: ${(props) => (props.sectionLevel ? props.theme.tableSectionColor : "inherit")};
        border-top: 1px solid ${(props) => {
        if (props.sectionLevel === 1) return props.theme.tableSectionMinorBorderColor
        if (props.sectionLevel === 2) return props.theme.tableSectionSecondaryBorderColor
        return props.theme.tableRowBorderColor
    }};
        transition: ${(props) => (props.transition ? `${props.transition}ms` : "none")};

        & > ${TableCellContentWrapper} {
            height: ${(props) => rowSize[props.size]};
            transition: ${(props) => (props.transition ? `${props.transition}ms` : "none")};
        }
    }

    ${(props) => props.rowHidden && `
        height: 0;

        & > ${TableCell} {
            border-top-width: 0px;

            & > ${TableCellContentWrapper} {
                padding-top: 0;
                padding-bottom: 0;
                height: 0;
            }
        }
    `}

    // fix height, border, hover color for inverted rows,
    ${(props) => props.inverted && `
        display: block;
        float: left;
        height: unset;
        min-width: fit-content;

        & > ${TableCell}, & > ${TableHeaderCell} {
            display: flex;
            align-items: center;
            height: ${rowSize[props.size]};
            border-top: 1px solid ${props.theme.tableRowBorderColor};

            &:first-child {
                border-top: none;
            }

            &::after {
                opacity: 0;
            }
        }
    `}

    &:hover {
        background: ${(props) => {
        if (props.noHover) return props.theme.tableRowBackground
        if (props.rowSelected) return props.theme.tableRowSelectedHoverBackground
        if (props.sectionLevel === 1) return props.theme.tableSectionMinorBackgroundHover
        if (props.sectionLevel === 2) return props.theme.tableSectionSecondaryBackgroundHover
        return props.theme.tableRowHoverBackground
    }};
    }

    &.drag-item {
        position: absolute;
        pointer-events: none;
        z-index: 1000;

        ${TableCell} {
            border-top: none;
        }
    }

    &.ghost {
        opacity: 0.5;
    }
`
export const TableBottom = styled.div`
    display: block;
    height: ${bottomHeight};
    color: ${(props) => props.theme.tableColor};
    background: ${(props) => props.theme.tableRowBackground};

    &.bottom-on-top {
        position: fixed;
        bottom: 0;
        right: 0;
        box-sizing: content-box;
        border-top: 4px solid ${(props) => props.theme.tableBottomBorderColor};
        box-shadow: 0 -10px 16px -12px #000;
    }
`
const TableBottomText = styled.div`
    line-height: ${bottomHeight};
    padding: 0 15px;
    font-family: ${(props) => props.theme.openSans};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    user-select: none;
`
export const TableSelected = styled(TableBottomText)`
    float: left;
`
export const TableTotal = styled(TableBottomText)`
    float: right;
`
export const TableBottomContent = styled.div`
    height: ${bottomHeight};
    padding: 0 15px;
    float: left;
    display: flex;
    align-items: center;
`


interface SortControlProps {
    show: boolean
    direction: OrderType
}
const SortControlWrapper = styled.span<SortControlProps>`
    margin-left: 8px;
    opacity: ${(props) => (props.show ? 1 : 0)};
    color: ${(props) => props.theme.tableSortControlColor};
    transition: ${(props) => props.theme.defaultTransition};
`
export const SortControl = ({
    show,
    direction,
}: SortControlProps) => (
    <SortControlWrapper className="sort" show={show} direction={direction}>
        {
            direction === ASC
                ? <ChevronUpIcon />
                : <ChevronDownIcon />
        }
    </SortControlWrapper>
)


interface TableMenuProps extends React.HTMLAttributes<HTMLDivElement> {
    cursor?: "grab" | "pointer"
}
const TableMenuWrapper = styled.div<TableMenuProps>`
    position: relative;
    height: 20px;
    width: 16px;
    padding-right: 2px;
    cursor: ${(props) => props.cursor || "pointer"};
`
export const TableMenu = ({
    cursor,
    ...other
}: TableMenuProps) => (
    <TableMenuWrapper cursor={cursor} {...other}>
        <Bars />
    </TableMenuWrapper>
)

const CollapseWrapper = styled.div<React.HTMLAttributes<HTMLDivElement>>`
    height: 18px;
    width: 18px;
    font-size: 10px;
    line-height: 10px;
    padding: 3px 4px;
    margin-right: 8px;
    border: 1px solid;
    border-radius: 50%;
    color: ${(props) => props.theme.tableCollapseButtonColor};
`
export const PlusButton = (props: React.HTMLAttributes<HTMLDivElement>) => (
    <CollapseWrapper {...props}>
        <PlusIcon />
    </CollapseWrapper>
)
export const MinusButton = (props: React.HTMLAttributes<HTMLDivElement>) => (
    <CollapseWrapper {...props}>
        <MinusIcon />
    </CollapseWrapper>
)


const SettingsWrapper = styled.div`
    width: fit-content;
    font-size: 16px;
    cursor: pointer;
    color: ${(props) => props.theme.tableSettingsButtonColor};
`
export const SettingsButton = (props: React.HTMLAttributes<HTMLDivElement>) => (
    <SettingsWrapper {...props}>
        <CogIcon />
    </SettingsWrapper>
)


export const InfiniteSpan = styled.span`
    font-family: ${(props) => props.theme.openSans};
    font-size: ${(props) => props.theme.fontSizes.md}px;
    color: ${(props) => props.theme.tableInfiniteColor};
`
export const Infinite = () => {
    const lang = useSelector((state: StoreType) => state.system.lang)
    return (
        <InfiniteSpan>
            {lang.infiniteRecursion}
        </InfiniteSpan>
    )
}
